/**
 * Public support endpoints are implemented by your backend (not this Next.js app).
 * Configure base URL via NEXT_PUBLIC_SUPPORT_API_BASE_URL, e.g.
 * https://api.example.com/api/support
 *
 * Expected routes (relative to that base, no trailing slash):
 * - POST /          → submit (header Idempotency-Key)
 * - POST /verify-otp
 * - POST /resend-otp
 *
 * If unset, falls back to: {NEXT_PUBLIC_API_BASE_URL}/api/support
 */

function trimSlash(s: string) {
	return s.replace(/\/+$/, "");
}

export function getSupportApiBaseUrl(): string {
	const explicit = process.env.NEXT_PUBLIC_SUPPORT_API_BASE_URL;
	if (explicit) {
		return trimSlash(explicit);
	}
	const api =
		trimSlash(
			process.env.NEXT_PUBLIC_API_BASE_URL ||
				"https://api.taxlienvest.com"
		) + "/support-requests";
	return trimSlash(api);
}

export type SupportSubmitBody = {
	name: string;
	email: string;
	message: string;
	phone?: string | null;
};

export type SupportSubmitResponse = {
	success?: boolean;
	request_id: string;
	message?: string;
	duplicate?: boolean;
	is_verified?: boolean;
	case_number?: string;
};

export type SupportVerifyBody = {
	request_id: string;
	otp: string;
};

export type SupportResendBody = {
	request_id: string;
};

export async function submitSupportRequest(
	idempotencyKey: string,
	body: SupportSubmitBody
): Promise<SupportSubmitResponse> {
	const base = getSupportApiBaseUrl();
	const res = await fetch(base, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			"Idempotency-Key": idempotencyKey,
		},
		body: JSON.stringify(body),
	});

	const data = (await res.json().catch(() => ({}))) as SupportSubmitResponse &
		Record<string, unknown>;

	if (!res.ok) {
		const err = data as { error?: string; message?: string };
		throw new Error(
			err.message || err.error || `Request failed (${res.status})`
		);
	}

	if (!data.request_id) {
		throw new Error("Invalid response: missing request_id");
	}

	return data;
}

export async function verifySupportOtp(
	body: SupportVerifyBody
): Promise<{ success?: boolean; message?: string }> {
	const base = getSupportApiBaseUrl();
	const res = await fetch(`${base}/verify-otp`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	const data = (await res.json().catch(() => ({}))) as {
		success?: boolean;
		message?: string;
		error?: string;
	};

	if (!res.ok) {
		throw new Error(
			data.message || data.error || `Verify failed (${res.status})`
		);
	}

	return data;
}

export async function resendSupportOtp(
	body: SupportResendBody
): Promise<{ success?: boolean; message?: string }> {
	const base = getSupportApiBaseUrl();
	const res = await fetch(`${base}/resend-otp`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	const data = (await res.json().catch(() => ({}))) as {
		success?: boolean;
		message?: string;
		error?: string;
	};

	if (!res.ok) {
		throw new Error(
			data.message || data.error || `Resend failed (${res.status})`
		);
	}

	return data;
}
